import Quill from 'quill';

export default class ImageResize {
  static moduleName = 'imageResize';
  quill: any;
  options: any;
  img: HTMLImageElement | null = null;
  overlay: HTMLDivElement | null = null;
  toolbar: HTMLDivElement | null = null;
  handles: HTMLDivElement[] = [];

  // Table Resizing State
  resizingType: 'col' | 'row' | null = null;
  resizingCell: HTMLTableCellElement | null = null;
  startPos = 0;
  startSize = 0;
  guideLine: HTMLDivElement | null = null;

  // Table Action State (Resize highlighting)
  hoverMarker: HTMLDivElement | null = null;
  hoverType: 'col' | 'row' | null = null;
  hoverCell: HTMLTableCellElement | null = null;

  constructor(quill: any, options: any = {}) {
    this.quill = quill;
    this.options = options;

    this.quill.root.addEventListener('click', this.handleClick, false);
    this.quill.root.addEventListener('mousemove', this.handleMouseMove, false);
    this.quill.root.addEventListener('mousedown', this.handleMouseDown, false);
    this.quill.root.addEventListener('mouseleave', () => this.hideHoverElements(), false);

    // Listen for selection changes to hide overlay when selection moves
    this.quill.on('selection-change', (range: any) => {
      if (!range && this.img) {
        this.hide();
      }
    });

    const container = this.quill.root.parentNode;
    if (container) {
      container.style.position = container.style.position || 'relative';
    }
  }

  handleMouseMove = (e: MouseEvent) => {
    if (this.resizingType) return;

    const target = e.target as HTMLElement;
    const cell = target.closest('td, th') as HTMLTableCellElement;

    if (cell) {
      const rect = cell.getBoundingClientRect();
      const padding = 8;
      const isRightEdge = Math.abs(e.clientX - rect.right) < padding;
      const isBottomEdge = Math.abs(e.clientY - rect.bottom) < padding;

      if (isRightEdge || isBottomEdge) {
        this.hoverType = isRightEdge ? 'col' : 'row';
        this.hoverCell = cell;
        this.showHoverElements();
        this.quill.root.style.cursor = isRightEdge ? 'col-resize' : 'row-resize';
      } else {
        this.hideHoverElements();
        this.quill.root.style.cursor = '';
      }
    } else {
      this.hideHoverElements();
      if (['col-resize', 'row-resize'].includes(this.quill.root.style.cursor)) {
        this.quill.root.style.cursor = '';
      }
    }
  };

  showHoverElements = () => {
    if (!this.hoverCell) return;
    const cell = this.hoverCell;
    const type = this.hoverType;
    const rect = cell.getBoundingClientRect();
    const parentRect = this.quill.root.parentNode.getBoundingClientRect();
    const tableRect = cell.closest('table')!.getBoundingClientRect();

    // Create Marker Line (Thicker like Google Docs)
    if (!this.hoverMarker) {
      this.hoverMarker = document.createElement('div');
      this.hoverMarker.setAttribute('style', 'position: absolute; background: #1890ff; z-index: 999; pointer-events: none; opacity: 0.8;');
      this.quill.root.parentNode.appendChild(this.hoverMarker);
    }

    if (type === 'col') {
      const top = tableRect.top - parentRect.top;
      const left = rect.right - parentRect.left - 1;
      Object.assign(this.hoverMarker.style, {
        left: `${left}px`, top: `${top}px`, width: '2px', height: `${tableRect.height}px`, display: 'block'
      });
    } else {
      const left = tableRect.left - parentRect.left;
      const top = rect.bottom - parentRect.top - 1;
      Object.assign(this.hoverMarker.style, {
        left: `${left}px`, top: `${top}px`, width: `${tableRect.width}px`, height: '2px', display: 'block'
      });
    }
  };

  hideHoverElements = () => {
    if (this.hoverMarker) this.hoverMarker.style.display = 'none';
    this.hoverType = null;
    this.hoverCell = null;
  };

  handleMouseDown = (e: MouseEvent) => {
    if (this.quill.root.style.cursor === 'col-resize' || this.quill.root.style.cursor === 'row-resize') {
      const cell = this.hoverCell || (e.target as HTMLElement).closest('td, th') as HTMLTableCellElement;
      if (!cell) return;

      e.preventDefault();
      e.stopPropagation();

      const type = this.quill.root.style.cursor === 'col-resize' ? 'col' : 'row';
      this.resizingType = type;
      this.resizingCell = cell;
      this.startPos = type === 'col' ? e.clientX : e.clientY;
      const rect = cell.getBoundingClientRect();
      this.startSize = type === 'col' ? rect.width : rect.height;

      // Ensure stable layout
      const table = cell.closest('table');
      if (table && type === 'col' && table.style.width === '100%') {
        table.style.width = `${table.offsetWidth}px`;
        table.style.tableLayout = 'fixed';
      }

      this.showGuideLine(e);

      document.body.style.cursor = this.quill.root.style.cursor;
      window.addEventListener('mousemove', this.handleDrag, { passive: false });
      window.addEventListener('mouseup', this.handleMouseUp, { passive: false });
    }
  };

  showGuideLine = (e: MouseEvent) => {
    if (this.guideLine) return;
    const table = this.resizingCell!.closest('table');
    if (!table) return;

    this.guideLine = document.createElement('div');
    const rect = table.getBoundingClientRect();
    const parentRect = this.quill.root.parentNode.getBoundingClientRect();

    const commonStyle = 'position: absolute; background: #1890ff; z-index: 10000; pointer-events: none;';

    if (this.resizingType === 'col') {
      const top = rect.top - parentRect.top;
      const height = rect.height;
      this.guideLine.setAttribute('style', `${commonStyle} width: 2px; top: ${top}px; height: ${height}px;`);

      // Handle icon
      const handle = document.createElement('div');
      handle.setAttribute('style', 'position: absolute; width: 12px; height: 32px; background: #1890ff; border-radius: 4px; left: -5px; top: 50%; transform: translateY(-50%); display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 4px rgba(0,0,0,0.2);');
      handle.innerHTML = '<svg width="8" height="12" viewBox="0 0 10 16" fill="white"><path d="M1 4h8v2H1V4zm0 6h8v2H1v-2z" /></svg>';
      this.guideLine.appendChild(handle);
    } else {
      const left = rect.left - parentRect.left;
      const width = rect.width;
      this.guideLine.setAttribute('style', `${commonStyle} height: 2px; left: ${left}px; width: ${width}px;`);

      // Handle icon
      const handle = document.createElement('div');
      handle.setAttribute('style', 'position: absolute; width: 32px; height: 12px; background: #1890ff; border-radius: 4px; top: -5px; left: 50%; transform: translateX(-50%); display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 4px rgba(0,0,0,0.2);');
      handle.innerHTML = '<svg width="12" height="8" viewBox="0 0 16 10" fill="white"><path d="M4 1h2v8H4V1zm6 0h2v8h-2V1z" /></svg>';
      this.guideLine.appendChild(handle);
    }

    this.quill.root.parentNode.appendChild(this.guideLine);
    this.updateGuideLine(e);
  };

  updateGuideLine = (e: MouseEvent) => {
    if (!this.guideLine) return;
    const parentRect = this.quill.root.parentNode.getBoundingClientRect();

    if (this.resizingType === 'col') {
      this.guideLine.style.left = `${e.clientX - parentRect.left}px`;
    } else {
      this.guideLine.style.top = `${e.clientY - parentRect.top}px`;
    }
  };

  handleDrag = (e: MouseEvent) => {
    if (!this.resizingType || !this.resizingCell) return;

    const type = this.resizingType;
    const cell = this.resizingCell;
    const startPos = this.startPos;
    const startSize = this.startSize;

    requestAnimationFrame(() => {
      if (!this.resizingType) return;
      this.updateGuideLine(e);

      const currentPos = type === 'col' ? e.clientX : e.clientY;
      const delta = currentPos - startPos;
      const newSize = Math.max(25, startSize + delta);

      if (type === 'col') {
        cell.style.width = `${newSize}px`;
        cell.style.minWidth = `${newSize}px`;
      } else {
        cell.style.height = `${newSize}px`;
        cell.style.minHeight = `${newSize}px`;
      }

      if (this.img) this.repositionEntities();
    });
  };

  handleMouseUp = () => {
    window.removeEventListener('mousemove', this.handleDrag);
    window.removeEventListener('mouseup', this.handleMouseUp);

    document.body.style.cursor = '';

    if (this.guideLine) {
      if (this.guideLine.parentNode) {
        this.guideLine.parentNode.removeChild(this.guideLine);
      }
      this.guideLine = null;
    }

    this.resizingType = null;
    this.resizingCell = null;
    this.quill.update('user');
  };

  handleClick = (evt: MouseEvent) => {
    const target = evt.target as HTMLElement;
    if (target && target.tagName === 'IMG') {
      if (this.img === target) return;
      if (this.img) this.hide();
      this.show(target as HTMLImageElement);
    } else if (this.img && !target?.closest('.ql-image-resize-toolbar')) {
      this.hide();
    }
  };

  show = (img: HTMLImageElement) => {
    this.img = img;
    this.showOverlay();
    this.showToolbar();
  };

  hide = () => {
    this.hideOverlay();
    this.hideToolbar();
    this.img = null;
  };

  showOverlay = () => {
    if (this.overlay) return;

    this.overlay = document.createElement('div');
    this.overlay.setAttribute('style', 'position: absolute; border: 1.5px solid #1890ff; box-sizing: border-box; pointer-events: none; z-index: 10;');
    this.quill.root.parentNode.appendChild(this.overlay);

    this.repositionEntities();

    this.createHandles();

    this.quill.root.addEventListener('scroll', this.repositionEntities);
    window.addEventListener('resize', this.repositionEntities);
  };

  hideOverlay = () => {
    if (!this.overlay) return;

    this.quill.root.parentNode.removeChild(this.overlay);
    this.overlay = null;
    this.handles = [];

    this.quill.root.removeEventListener('scroll', this.repositionEntities);
    window.removeEventListener('resize', this.repositionEntities);
  };

  showToolbar = () => {
    if (this.toolbar) return;

    this.toolbar = document.createElement('div');
    this.toolbar.classList.add('ql-image-resize-toolbar');
    this.toolbar.setAttribute('style', 'position: absolute; background: white; border: 1px solid #ddd; border-radius: 6px; box-shadow: 0 3px 10px rgba(0,0,0,0.1); display: flex; gap: 4px; padding: 4px; z-index: 12; pointer-events: auto; transition: top 0.2s, left 0.2s;');

    const alignments = [
      { label: 'Left', value: 'left', icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="17" y1="10" x2="3" y2="10"></line><line x1="21" y1="6" x2="3" y2="6"></line><line x1="21" y1="14" x2="3" y2="14"></line><line x1="17" y1="18" x2="3" y2="18"></line></svg>' },
      { label: 'Center', value: 'center', icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="10" x2="6" y2="10"></line><line x1="21" y1="6" x2="3" y2="6"></line><line x1="21" y1="14" x2="3" y2="14"></line><line x1="18" y1="18" x2="6" y2="18"></line></svg>' },
      { label: 'Right', value: 'right', icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="21" y1="10" x2="7" y2="10"></line><line x1="21" y1="6" x2="3" y2="6"></line><line x1="21" y1="14" x2="3" y2="14"></line><line x1="21" y1="18" x2="7" y2="18"></line></svg>' },
    ];

    alignments.forEach(align => {
      const btn = document.createElement('button');
      btn.innerHTML = align.icon;
      btn.title = align.label;
      btn.setAttribute('style', 'background: none; border: none; cursor: pointer; padding: 6px; display: flex; align-items: center; border-radius: 4px; color: #555; transition: all 0.2s;');

      const updateActiveState = () => {
        const isCurrent = (align.value === 'left' && this.img?.style.float === 'left') ||
          (align.value === 'center' && this.img?.style.margin === '0px auto') ||
          (align.value === 'right' && this.img?.style.float === 'right');
        btn.style.backgroundColor = isCurrent ? '#e6f7ff' : 'transparent';
        btn.style.color = isCurrent ? '#1890ff' : '#555';
      };

      updateActiveState();

      btn.addEventListener('mouseover', () => {
        if (btn.style.backgroundColor !== 'rgb(230, 247, 255)') {
          btn.style.backgroundColor = '#f5f5f5';
        }
      });
      btn.addEventListener('mouseout', () => {
        if (btn.style.backgroundColor !== 'rgb(230, 247, 255)') {
          btn.style.backgroundColor = 'transparent';
        }
      });
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.setAlignment(align.value);
        this.hideToolbar();
        this.showToolbar();
      });
      this.toolbar?.appendChild(btn);
    });

    // Add Divider
    const divider = document.createElement('div');
    divider.setAttribute('style', 'width: 1px; height: 18px; background: #eee; margin: 4px 2px;');
    this.toolbar?.appendChild(divider);

    // Add Delete Button
    const deleteBtn = document.createElement('button');
    deleteBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>';
    deleteBtn.title = 'Delete Image';
    deleteBtn.setAttribute('style', 'background: none; border: none; cursor: pointer; padding: 6px; display: flex; align-items: center; border-radius: 4px; color: #ff4d4f; transition: all 0.2s;');

    deleteBtn.addEventListener('mouseover', () => deleteBtn.style.backgroundColor = '#fff1f0');
    deleteBtn.addEventListener('mouseout', () => deleteBtn.style.backgroundColor = 'transparent');
    deleteBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this.img) {
        let blot = Quill.find(this.img) as any;
        if (!blot) {
          // If we couldn't find the blot directly on the image, try walking up the DOM tree
          let parentNode = this.img.parentNode;
          while (parentNode && parentNode !== this.quill.root) {
            blot = Quill.find(parentNode);
            if (blot) break;
            parentNode = parentNode.parentNode;
          }
        }
        if (blot && typeof blot.remove === 'function') {
          blot.remove();
          this.quill.update('user');
        }
        this.hide();
      }
    });
    this.toolbar?.appendChild(deleteBtn);

    this.quill.root.parentNode.appendChild(this.toolbar);
    this.repositionEntities();
  };

  hideToolbar = () => {
    if (!this.toolbar) return;
    this.quill.root.parentNode.removeChild(this.toolbar);
    this.toolbar = null;
  };

  setAlignment = (value: string) => {
    if (!this.img) return;

    if (value === 'left') {
      this.img.style.display = 'block';
      this.img.style.margin = '0';
      this.img.style.float = 'left';
    } else if (value === 'center') {
      this.img.style.display = 'block';
      this.img.style.margin = '0 auto';
      this.img.style.float = 'none';
      this.img.style.clear = 'both';
    } else if (value === 'right') {
      this.img.style.display = 'block';
      this.img.style.margin = '0 0 0 auto';
      this.img.style.float = 'right';
    }
    this.quill.update('user');
    setTimeout(() => this.repositionEntities(), 0);
  };

  repositionEntities = () => {
    if (!this.img) return;

    const container = this.quill.root.parentNode;
    const parentRect = container.getBoundingClientRect();
    const imgRect = this.img.getBoundingClientRect();

    if (this.overlay) {
      Object.assign(this.overlay.style, {
        left: `${imgRect.left - parentRect.left}px`,
        top: `${imgRect.top - parentRect.top}px`,
        width: `${imgRect.width}px`,
        height: `${imgRect.height}px`,
      });
    }

    if (this.toolbar) {
      Object.assign(this.toolbar.style, {
        left: `${imgRect.left - parentRect.left + (imgRect.width / 2) - (this.toolbar.offsetWidth / 2)}px`,
        top: `${imgRect.top - parentRect.top + imgRect.height + 12}px`,
      });
    }
  };

  createHandles = () => {
    const handlePositions = [
      { top: '-4px', left: '-4px', cursor: 'nwse-resize' },    // 0: Top-left
      { top: '-4px', right: '-4px', cursor: 'nesw-resize' },   // 1: Top-right
      { bottom: '-4px', left: '-4px', cursor: 'nesw-resize' }, // 2: Bottom-left
      { bottom: '-4px', right: '-4px', cursor: 'nwse-resize' },// 3: Bottom-right
      { top: '-4px', left: '50%', marginLeft: '-4px', cursor: 'ns-resize' },    // 4: Top-center
      { bottom: '-4px', left: '50%', marginLeft: '-4px', cursor: 'ns-resize' }, // 5: Bottom-center
      { top: '50%', left: '-4px', marginTop: '-4px', cursor: 'ew-resize' },     // 6: Left-center
      { top: '50%', right: '-4px', marginTop: '-4px', cursor: 'ew-resize' },    // 7: Right-center
    ];

    handlePositions.forEach((pos, index) => {
      const handle = document.createElement('div');
      let styleStr = 'position: absolute; width: 8px; height: 8px; background-color: white; border: 1.5px solid #1890ff; border-radius: 1px; box-shadow: 0 0 3px rgba(0,0,0,0.2); pointer-events: auto; z-index: 11;';
      Object.entries(pos).forEach(([key, val]) => {
        styleStr += ` ${key}: ${val};`;
      });
      handle.setAttribute('style', styleStr);

      handle.addEventListener('mousedown', (evt: MouseEvent) => this.handleMousedown(evt, index), false);
      this.overlay?.appendChild(handle);
      this.handles.push(handle);
    });
  };

  handleMousedown = (evt: MouseEvent, index: number) => {
    evt.preventDefault();
    evt.stopPropagation();

    const startX = evt.clientX;
    const startY = evt.clientY;
    const startWidth = this.img?.clientWidth || this.img?.width || 0;
    const startHeight = this.img?.clientHeight || this.img?.height || 0;
    const aspectRatio = startWidth / startHeight;

    let requestID: number | null = null;

    const drag = (moveEvt: MouseEvent) => {
      if (!this.img) return;

      if (requestID) cancelAnimationFrame(requestID);

      requestID = requestAnimationFrame(() => {
        const deltaX = moveEvt.clientX - startX;
        const deltaY = moveEvt.clientY - startY;

        let newWidth = startWidth;
        let newHeight = startHeight;

        // Width calculation
        if ([0, 2, 6].includes(index)) { // Left side
          newWidth = startWidth - deltaX;
        } else if ([1, 3, 7].includes(index)) { // Right side
          newWidth = startWidth + deltaX;
        }

        // Height calculation
        if ([0, 1, 4].includes(index)) { // Top side
          newHeight = startHeight - deltaY;
        } else if ([2, 3, 5].includes(index)) { // Bottom side
          newHeight = startHeight + deltaY;
        }

        if (newWidth < 20) newWidth = 20;
        if (newHeight < 20) newHeight = 20;

        // Maintain aspect ratio for corner handles
        if (index < 4) {
          if (Math.abs(deltaX) > Math.abs(deltaY)) {
            newHeight = newWidth / aspectRatio;
          } else {
            newWidth = newHeight * aspectRatio;
          }
        }

        this.img!.style.width = `${newWidth}px`;
        if ([0, 1, 2, 3, 4, 5].includes(index)) {
          this.img!.style.height = `${newHeight}px`;
        }

        this.repositionEntities();
      });
    };

    const stopDrag = () => {
      if (requestID) cancelAnimationFrame(requestID);
      window.removeEventListener('mousemove', drag);
      window.removeEventListener('mouseup', stopDrag);
      this.quill.update('user');
    };

    window.addEventListener('mousemove', drag);
    window.addEventListener('mouseup', stopDrag);
  };
}
